import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { trackPageView } from "../../services/gtm";
import { Menu, X, Rocket, Home, MessageCircle, HelpCircle, FileText, Globe } from "lucide-react";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ro' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.__gtm_initial_tracked) {
      delete window.__gtm_initial_tracked;
      return;
    }
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  const navLinks = [
    { name: t('nav.home'), path: "/home", icon: Home },
    { name: t('nav.create_story'), path: "/create", icon: Rocket },
  ];

  return (
    <div className="min-h-screen bg-[#f8f1e9] text-neutral-900 flex flex-col font-story">
      {/* Skip to Content Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-6 focus:py-3 focus:bg-teal-600 focus:text-white focus:rounded-xl focus:shadow-2xl focus:font-bold transition-all"
      >
        Skip to main content
      </a>

      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-[#f8f1e9]/90 backdrop-blur-md border-b border-neutral-200/50 shadow-sm">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">

          {/* Logo */}
          <Link to="/home" className="flex items-center gap-2 group">
            <div className="w-12 h-12 md:w-20 md:h-20 overflow-hidden rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <img src="/logo.png" alt="DreamTales Logo" className="w-full h-full object-cover" />
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path || (link.path === '/home' && location.pathname === '/');
              return (
                <Link key={link.path} to={link.path}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className={`font-bold ${isActive ? "bg-teal-100 text-teal-800" : "text-neutral-600 hover:text-teal-700 hover:bg-teal-50"}`}
                  >
                    {link.name}
                  </Button>
                </Link>
              );
            })}

            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center gap-2 font-bold text-neutral-600 hover:text-teal-700 hover:bg-teal-50 ml-4"
            >
              <Globe className="w-4 h-4" />
              {i18n.language?.toUpperCase().includes('EN') ? 'RO' : 'EN'}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="md:hidden p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-neutral-200 bg-white/95 backdrop-blur-xl absolute w-full left-0 shadow-lg animate-in slide-in-from-top-2">
            <div className="px-4 py-6 space-y-2">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path || (link.path === '/home' && location.pathname === '/');
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${isActive
                      ? "bg-teal-50 text-teal-700 shadow-sm"
                      : "text-neutral-600 hover:bg-neutral-50"
                      }`}
                  >
                    <link.icon className="w-5 h-5" />
                    {link.name}
                  </Link>
                );
              })}

              <button
                onClick={toggleLanguage}
                className="flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-neutral-600 hover:bg-neutral-50 w-full"
              >
                <Globe className="w-5 h-5" />
                {i18n.language?.toUpperCase().includes('EN') ? 'Română (RO)' : 'English (EN)'}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main id="main-content" className="flex-grow w-full">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-teal-900 mt-auto">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-2">
              <div className="w-16 h-16 overflow-hidden rounded-lg flex items-center justify-center shadow-lg">
                <img src="/logo.png" alt="DreamTales Logo" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm font-medium text-teal-100/60">
              <Link to="/faq" className="hover:text-teal-400 transition-colors flex items-center gap-1"><HelpCircle className="w-4 h-4" /> {t('nav.faq')}</Link>
              <Link to="/privacy" className="hover:text-teal-400 transition-colors flex items-center gap-1"><FileText className="w-4 h-4" /> {t('nav.privacy')}</Link>
              <Link to="/terms" className="hover:text-teal-400 transition-colors flex items-center gap-1"><FileText className="w-4 h-4" /> {t('nav.terms')}</Link>
              <Link to="/contact" className="hover:text-teal-400 transition-colors flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {t('nav.contact')}</Link>
            </div>

            <div className="flex flex-col items-center md:items-end gap-1">
              <p className="text-sm text-teal-100/40">
                © {new Date().getFullYear()} DreamTales. {t('footer.rights')}
              </p>
              <p className="text-xs text-teal-100/20">
                {t('footer.built_by')} <a href="https://github.com/ionuthub" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">Ionut Dobrin</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
